import { oc } from "@orpc/contract";
import { z } from "zod";

export const DemandArchetypeSchema = z.enum([
  "cut_to_length",
  "whole_package",
  "unknown",
]);

export const ClassificationStatusSchema = z.enum([
  "candidate",
  "confirmed",
  "rejected",
]);

/**
 * Continuity `k` is 1–3 in practice (at most k+1 pieces). Cap well below
 * Postgres INTEGER so a huge write is a 400, not a 500.
 */
export const MAX_JOINS = 9;

export const MaxJoinsSchema = z.number().int().min(0).max(MAX_JOINS);

export const MillableClassificationSchema = z.object({
  id: z.string().uuid(),
  productV3Key: z.string(),
  archetype: DemandArchetypeSchema,
  status: ClassificationStatusSchema,
  defaultMaxJoins: MaxJoinsSchema.nullable(),
  p50: z.number().nullable(),
  p90: z.number().nullable(),
  maxLine: z.number().nullable(),
  maxUnit: z.number().nullable(),
  unitsUnder20: z.number().nullable(),
  unitCount: z.number().nullable(),
  soLines: z.number().nullable(),
  scoredAt: z.coerce.date().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  reviewedBy: z.string().uuid().nullable(),
});

export const millableClassificationsContracts = {
  list: oc
    .input(
      z.object({
        status: ClassificationStatusSchema.optional(),
        archetype: DemandArchetypeSchema.optional(),
        productV3Key: z.string().optional(),
      }),
    )
    .output(
      z.object({ classifications: z.array(MillableClassificationSchema) }),
    )
    .route({
      method: "GET",
      summary: "List millable product classifications",
      description:
        "Lists products classified as cut-to-length, whole-package or unknown demand, with sales-size statistics and review status. Filter by status, archetype or SKU.",
    }),
  getForProduct: oc
    .input(z.object({ productV3Key: z.string().min(1) }))
    .output(
      z.object({ classification: MillableClassificationSchema.nullable() }),
    )
    .route({
      method: "GET",
      summary: "Get a product's millable classification",
      description:
        "Returns the cut-to-length vs whole-package demand classification for one SKU, with sales-size statistics, default max joins and review status, or null.",
    }),
  score: oc
    .input(z.object({}))
    .output(
      z.object({
        upserted: z.number().int(),
        candidates: z.number().int(),
        reason: z.enum(["no_lot_snapshots", "no_matching_history"]).nullable(),
      }),
    )
    .route({
      method: "POST",
      summary: "Score millable product classifications",
      description:
        "Re-scores the organization's lot-tracked products from sales history into candidate cut-to-length or whole-package classifications; returns counts or why nothing was scored.",
    }),
  confirm: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ classification: MillableClassificationSchema }))
    .route({
      method: "POST",
      summary: "Confirm a millable classification",
      description:
        "Marks a candidate cut-to-length or whole-package product classification as confirmed, recording the current user as reviewer.",
    }),
  reject: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ classification: MillableClassificationSchema }))
    .route({
      method: "POST",
      summary: "Reject a millable classification",
      description:
        "Marks a candidate cut-to-length or whole-package product classification as rejected, recording the current user as reviewer.",
    }),
  updateLineMaxJoins: oc
    .input(
      z.object({
        salesOrderId: z.string().uuid(),
        lineItemId: z.string().uuid(),
        maxJoins: MaxJoinsSchema.nullable(),
      }),
    )
    .output(z.object({ maxJoins: MaxJoinsSchema.nullable() }))
    .route({
      method: "POST",
      summary: "Set max joins on a sales order line",
      description:
        "Sets or clears how many joins (pieces minus one, 0–9) a sales order line accepts when filled from cut lengths.",
    }),
} as const;

export type MillableClassification = z.infer<
  typeof MillableClassificationSchema
>;
export type DemandArchetype = z.infer<typeof DemandArchetypeSchema>;
export type ClassificationStatus = z.infer<typeof ClassificationStatusSchema>;
